import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { requestMobile } from '@/lib/request'
import { usePersistentState } from '@/hooks/usePersistentState'
import { toast } from 'sonner'
import {
  RefreshCw,
  ChevronRight,
  Wallet,
  Clock,
  Activity,
  Edit,
  Play,
  Square,
  Filter,
  X,
  Rows3,
  LayoutGrid,
} from 'lucide-react'

interface Task {
  id: string
  name: string
  plat: string
  status: string
  account: string
  balance?: number
  last_bet?: number
  strategy: string
  use_count?: number
  current_count?: number
  mode?: string
  max?: number
  strategy_hook?: string
  next?: string
  update_time?: string
  wd_count?: number
  wd_day_count?: number
  node?: string
  account_id?: string
}

export default function TaskList() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [pageNum, setPageNum] = useState(1)
  const latestRequestRef = useRef(0)
  const latestAppliedRequestRef = useRef(0)
  const navigate = useNavigate()

  // 筛选状态
  const [showFilter, setShowFilter] = useState(false)
  const [filters, setFilters, clearFilters] = usePersistentState<Record<string, any>>(
    'mobile:task:filters',
    {},
  )
  const [viewMode, setViewMode] = usePersistentState<'compact' | 'cards'>(
    'mobile:task:view-mode',
    'compact',
  )
  const [platforms, setPlatforms] = useState<string[]>([])

  // 编辑对话框状态
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [editForm, setEditForm] = useState({
    current_count: 0,
    mode: '',
    balance: 0,
    wd_day_count: 0
  })

  const fetchTasks = async (page = 1, reset = false) => {
    const requestId = ++latestRequestRef.current
    setLoading(true)
    try {
      const resp = await requestMobile('/common/apis/v1/get_tasks', {
        page_num: page,
        page_size: 20,
        query_filter: filters
      })

      if (
        requestId > latestAppliedRequestRef.current &&
        resp &&
        !resp.code
      ) {
        latestAppliedRequestRef.current = requestId
        const newTasks = resp.data.list || []
        setTasks((currentTasks) => {
          const nextTasks = reset ? newTasks : [...currentTasks, ...newTasks]
          setHasMore(nextTasks.length < resp.data.total)
          return nextTasks
        })
        setPageNum(page)

        // 提取平台列表
        if (reset) {
          const uniquePlatforms = Array.from(new Set(newTasks.map((t: Task) => t.plat).filter(Boolean)))
          setPlatforms(uniquePlatforms as string[])
        }
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error)
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchTasks(1, true)
  }, [filters])

  const handleRefresh = () => {
    fetchTasks(1, true)
  }

  const handleFilterChange = (key: string, value: string) => {
    if (value === 'all') {
      const newFilters = { ...filters }
      delete newFilters[key]
      setFilters(newFilters)
    } else {
      setFilters({ ...filters, [key]: value })
    }
  }

  const handleResetFilters = () => {
    clearFilters()
  }

  const getActiveFilterCount = () => {
    return Object.keys(filters).length
  }

  const handleOpenEdit = (task: Task) => {
    setEditingTask(task)
    setEditForm({
      current_count: task.current_count || 0,
      mode: task.mode || '',
      balance: task.balance || 0,
      wd_day_count: task.wd_day_count || 0
    })
    setEditDialogOpen(true)
  }

  const handleUpdateTask = async () => {
    if (!editingTask) return

    try {
      const resp = await requestMobile('/system/api/update/task', {
        id: editingTask.id,
        current_count: editForm.current_count,
        mode: editForm.mode,
        balance: editForm.balance,
        wd_day_count: editForm.wd_day_count
      })
      if (resp && resp.code === 0) {
        toast.success('更新成功')
        setEditDialogOpen(false)
        fetchTasks(1, true)
      }
    } catch (error) {
      console.error('Failed to update task:', error)
    }
  }

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchTasks(pageNum + 1, false)
    }
  }

  const handleGoToDetail = (task: Task) => {
    navigate('/mobile/account/detail', {
      state: {
        remarks_name: task.account,
        plat: task.plat,
        task: task.name,
        task_id: task.id,
        node: task.node,
        account_id: task.account_id
      }
    })
  }

  const summary = tasks.reduce(
    (result, task) => ({
      running:
        result.running + (task.status === 'running' || task.status === 'runing' ? 1 : 0),
      stopped: result.stopped + (task.status === 'stop' ? 1 : 0),
      balance: result.balance + (task.balance || 0),
    }),
    { running: 0, stopped: 0, balance: 0 },
  )

  const getStatusBadge = (status: string) => {
    const statusMap: Record<
      string,
      {
        label: string
        variant: 'default' | 'secondary' | 'destructive' | 'outline'
        class?: string
        icon?: typeof Play
      }
    > = {
      running: {
        label: '运行中',
        variant: 'default',
        class: 'bg-green-500/10 text-green-500 border-green-500/20',
        icon: Play,
      },
      runing: {
        label: '运行中',
        variant: 'default',
        class: 'bg-green-500/10 text-green-500 border-green-500/20',
        icon: Play,
      },
      stop: {
        label: '已停止',
        variant: 'secondary',
        class: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
        icon: Square,
      },
    }
    const statusInfo = statusMap[status] || { label: status, variant: 'outline' as const }
    const Icon = statusInfo.icon
    return (
      <Badge variant={statusInfo.variant} className={statusInfo.class}>
        {Icon && <Icon className="h-3 w-3 mr-1" />}
        {statusInfo.label}
      </Badge>
    )
  }

  return (
    <div className="min-h-full bg-background">
      {/* 头部 */}
      <div className="bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-b" style={{ paddingTop: 'max(env(safe-area-inset-top), 16px)' }}>
        <div className="flex items-center justify-between p-4">
          <div>
            <h1 className="text-xl font-bold">任务列表</h1>
            <p className="text-sm text-muted-foreground">{tasks.length} 个任务</p>
          </div>
          <div className="flex gap-2">
            <div className="flex rounded-md border bg-background p-0.5">
              <Button
                variant={viewMode === 'compact' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                aria-label="紧凑列表"
                onClick={() => setViewMode('compact')}
              >
                <Rows3 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                aria-label="卡片列表"
                onClick={() => setViewMode('cards')}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilter(!showFilter)}
              className="relative"
            >
              <Filter className="h-4 w-4" />
              {getActiveFilterCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {getActiveFilterCount()}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* 筛选面板 */}
        {showFilter && (
          <div className="border-t border-border p-4 space-y-3 bg-muted/30">
            {/* 平台筛选 */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">平台</Label>
              <Select
                value={filters.plat || 'all'}
                onValueChange={(val) => handleFilterChange('plat', val)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="全部平台" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  {platforms.map((plat) => (
                    <SelectItem key={plat} value={plat}>
                      {plat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 模式筛选 */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">模式</Label>
              <Select
                value={filters.mode || 'all'}
                onValueChange={(val) => handleFilterChange('mode', val)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="全部模式" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="run">run</SelectItem>
                  <SelectItem value="rd">rd</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 状态筛选 */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">状态</Label>
              <Select
                value={filters.status || 'all'}
                onValueChange={(val) => handleFilterChange('status', val)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="全部状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="runing">运行中</SelectItem>
                  <SelectItem value="stop">已停止</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 操作按钮 */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={handleResetFilters}
                disabled={getActiveFilterCount() === 0}
              >
                <X className="h-3 w-3 mr-1" />
                重置
              </Button>
              <Button
                size="sm"
                className="flex-1"
                onClick={() => setShowFilter(false)}
              >
                完成
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 高密度任务列表 */}
      <div className="px-3 py-3 space-y-3">
        {tasks.length > 0 ? (
          <>
            <div className="grid grid-cols-4 gap-1 rounded-xl border bg-card p-2 shadow-sm">
              <CompactSummary label="已加载" value={tasks.length.toString()} />
              <CompactSummary
                label="运行"
                value={summary.running.toString()}
                valueClassName="text-green-500"
              />
              <CompactSummary
                label="停止"
                value={summary.stopped.toString()}
                valueClassName="text-muted-foreground"
              />
              <CompactSummary
                label="总余额"
                value={formatCompactNumber(summary.balance)}
                valueClassName="text-emerald-500"
              />
            </div>

            {viewMode === 'compact' ? (
            <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
              <div className="grid grid-cols-[minmax(0,1.7fr)_72px_68px_40px] items-center gap-2 border-b bg-muted/50 px-3 py-2 text-[11px] font-medium text-muted-foreground">
                <span>任务</span>
                <span className="text-right">余额</span>
                <span className="text-right">次数</span>
                <span />
              </div>
              {tasks.map((task) => (
                <div
                  key={task.id}
                  role="button"
                  tabIndex={0}
                  className={`grid min-h-[58px] grid-cols-[minmax(0,1.7fr)_72px_68px_40px] items-center gap-2 border-b px-3 py-2 text-left transition-colors last:border-b-0 ${
                    task.mode === 'rd'
                      ? 'bg-orange-500/[0.04] active:bg-orange-500/10'
                      : task.mode === 'run'
                        ? 'bg-blue-500/[0.04] active:bg-blue-500/10'
                        : 'active:bg-muted/70'
                  }`}
                  onClick={() => handleGoToDetail(task)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleGoToDetail(task)
                    }
                  }}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                        task.status === 'running' || task.status === 'runing'
                          ? 'bg-green-500'
                          : 'bg-muted-foreground/50'
                      }`}
                    />
                    <span className="truncate text-sm font-semibold">{task.name}</span>
                  </div>

                  <div className="text-right">
                    <div className="text-sm font-semibold text-emerald-500">
                      {formatCompactNumber(task.balance || 0)}
                    </div>
                    {task.max ? (
                      <div className="truncate text-[10px] text-muted-foreground">
                        限 {formatCompactNumber(task.max)}
                      </div>
                    ) : (
                      <div className="text-[10px] text-muted-foreground">余额</div>
                    )}
                  </div>

                  <div className="text-right">
                    <div className="text-sm font-semibold">{task.current_count || 0}</div>
                    {task.mode === 'rd' && task.wd_day_count !== undefined && (
                      <div className="text-[10px] text-orange-500">
                        {task.wd_day_count} 天
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      aria-label={`编辑 ${task.name}`}
                      onClick={(event) => {
                        event.stopPropagation()
                        handleOpenEdit(task)
                      }}
                    >
                      <Edit className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            ) : (
              <div className="space-y-4">
                {tasks.map((task) => (
                  <Card
                    key={task.id}
                    className="overflow-hidden border-border/40 hover:border-primary/20"
                  >
                    <CardHeader className="pb-3 bg-gradient-to-r from-card to-card/50">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base mb-1 truncate">{task.name}</h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="font-medium">{task.plat}</span>
                            <span className="text-xs">•</span>
                            <span className="truncate">{task.account}</span>
                          </div>
                        </div>
                        {getStatusBadge(task.status)}
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-4">
                      {task.mode && (
                        <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                          <div className="p-2 bg-blue-500/10 rounded-md">
                            <Activity className="h-4 w-4 text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground mb-0.5">运行模式</p>
                            <p className="font-semibold text-blue-400 truncate">{task.mode}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 p-3 bg-green-500/5 border border-green-500/10 rounded-lg">
                        <div className="p-2 bg-green-500/10 rounded-md">
                          <Wallet className="h-4 w-4 text-green-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-0.5">余额</p>
                          <p className="font-semibold text-green-500 text-lg truncate">
                            ¥{task.balance?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="text-center p-2.5 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">已运行</p>
                          <p className="font-semibold text-sm">{task.use_count || 0}</p>
                        </div>
                        <div className="text-center p-2.5 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">本次运行</p>
                          <p className="font-semibold text-sm">{task.current_count || 0}</p>
                        </div>
                      </div>

                      <div className="space-y-2 pt-2 border-t border-border/40">
                        {task.mode === 'rd' && task.wd_day_count !== undefined && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">随机天数</span>
                            <span className="font-medium text-orange-400">
                              {task.wd_day_count} 天
                            </span>
                          </div>
                        )}
                        {task.max && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">限额</span>
                            <span className="font-medium">¥{task.max}</span>
                          </div>
                        )}
                        {task.update_time && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>更新于 {task.update_time}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleOpenEdit(task)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          编辑
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => handleGoToDetail(task)}
                        >
                          查看详情
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {hasMore && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    加载中...
                  </>
                ) : (
                  '加载更多'
                )}
              </Button>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Activity className="h-12 w-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">{loading ? '加载中...' : '暂无任务'}</p>
          </div>
        )}
      </div>

      {/* 编辑任务对话框 */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>编辑任务</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* 本次运行 */}
            <div className="space-y-2">
              <Label htmlFor="current_count">本次运行</Label>
              <Input
                id="current_count"
                type="number"
                value={editForm.current_count}
                onChange={(e) => setEditForm({ ...editForm, current_count: Number(e.target.value) })}
              />
            </div>

            {/* 运行模式 */}
            <div className="space-y-2">
              <Label htmlFor="mode">运行模式</Label>
              <Select
                value={editForm.mode}
                onValueChange={(value) => setEditForm({ ...editForm, mode: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择运行模式" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="run">run</SelectItem>
                  <SelectItem value="rd">rd</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 余额 */}
            <div className="space-y-2">
              <Label htmlFor="balance">余额</Label>
              <Input
                id="balance"
                type="number"
                step="0.01"
                value={editForm.balance}
                onChange={(e) => setEditForm({ ...editForm, balance: Number(e.target.value) })}
              />
            </div>

            {/* 随机天数 */}
            <div className="space-y-2">
              <Label htmlFor="wd_day_count">随机天数</Label>
              <Input
                id="wd_day_count"
                type="number"
                value={editForm.wd_day_count}
                onChange={(e) => setEditForm({ ...editForm, wd_day_count: Number(e.target.value) })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleUpdateTask}>
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function CompactSummary({
  label,
  value,
  valueClassName = '',
}: {
  label: string
  value: string
  valueClassName?: string
}) {
  return (
    <div className="min-w-0 rounded-lg px-1 py-1.5 text-center">
      <div className={`truncate text-sm font-bold ${valueClassName}`}>{value}</div>
      <div className="truncate text-[10px] text-muted-foreground">{label}</div>
    </div>
  )
}

function formatCompactNumber(value: number) {
  return new Intl.NumberFormat('zh-CN', {
    notation: Math.abs(value) >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value)
}
